import React, { useEffect, useMemo, useRef, useState } from "react";
import { RecommendationBand as AssetBand } from "../../recommendations/components/Bands/RecommendationBand";
import { CkeditorConfiguration } from "../../smartCharts/CkEditorConfig";
import { MyUploadAdapterPlugin } from "../../smartCharts/CustomImageUploadPlugin";
import RecommendationNoteEdit from "../../common/components/RecommendationNoteEdit";
import Portal from "../../common/components/Portal";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build";
import SelectBox from "../../images/components/SelectBox";
import { useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import moment from "moment";
import RecommendationNoteView from "../../common/components/RecommendationNoteView";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { isEqual } from "lodash";
import CopyIcon from "../../../assets/img/copy-move.svg";
import ReactTooltip from "react-tooltip";
import { sanitizeHtml } from "../../common/components/SafeHtml";

const MobileRecommendationForm = ({
    data = {},
    isMobileRecommendation = false,
    showFields,
    fieldSearch = "",
    showType = null,
    setShowBand,
    getSuggestionDropdown = {},
    renderFormFields,
    showBand,
    setShowEmptyFieldsFillModal,
    handleChange,
    shouldShowField = () => true
}) => {
    const { section } = useParams();
    const [showNoteModal, setShowNoteModal] = useState(false);
    const templateRef = useRef(null);
    const [editorHeight, setEditorHeight] = useState(null);
    const { getMobileRecommendationByIdResponse } = useSelector(state => state.mobileRecommendationsReducer);
    const { getAssetVoiceNotesResponse } = useSelector(state => state.assetSurveyProjectReducer);
    const [editorData, setEditorData] = useState(data?.Narrative?.notes?.value ?? "");
    const [activeNarrativeTab, setActiveNarrativeTab] = useState("mobile_narrative");
    const prevDepsRef = useRef({
        data: null,
        suggestionData: null,
        renderFormFields: null,
        fieldSearch: "",
        showType: null,
        showFields: null,
        showBand: null,
        shouldShowField: null,
        result: null
    });
    const audioText =
        activeNarrativeTab === "mobile_narrative"
            ? [...(getAssetVoiceNotesResponse?.data?.audio ?? [])]?.reduce((acc, item) => {
                  acc = acc ? `${acc} <br/><br/> ${item?.voice_text ?? ""}` : (item?.voice_text ?? "");
                  return acc;
              }, "") || ""
            : activeNarrativeTab === "ai_recom"
              ? getMobileRecommendationByIdResponse?.recommendation?.ai_recommendation
              : getMobileRecommendationByIdResponse?.recommendation?.ai_recommendation_narrative || "";

    useEffect(() => {
        const timeout = setTimeout(() => {
            const height = (templateRef.current?.clientHeight || 0) - 340;
            setEditorHeight(height > 0 ? height : 150);
        }, 500);

        return () => clearTimeout(timeout);
    }, [showBand]);

    useEffect(() => {
        if (data?.Narrative?.notes?.value && data?.Narrative?.notes?.value !== editorData) {
            setEditorData(data?.Narrative?.notes?.value);
        }
    }, [data?.Narrative?.notes?.value]);

    const openReportNote = name => {
        setShowNoteModal(name);
    };
    const renderNote = () => {
        if (!showNoteModal) return null;

        return showNoteModal !== "mob-rec" ? (
            <Portal
                body={
                    <RecommendationNoteEdit
                        notes={data?.Narrative?.notes?.value || ""}
                        onCancel={() => setShowNoteModal(false)}
                        isRecommendationView={true}
                        handleChangeNote={data => {
                            handleChange({ name: "notes", value: data });
                        }}
                        heading={
                            activeNarrativeTab === "mobile_narrative"
                                ? "Voice Notes"
                                : activeNarrativeTab === "ai_recom"
                                  ? "AI Recommendation"
                                  : "Recommendation Narrative"
                        }
                    />
                }
                onCancel={() => setShowNoteModal(false)}
            />
        ) : (
            <Portal
                body={
                    <RecommendationNoteView
                        notes={audioText}
                        onCancel={() => setShowNoteModal(false)}
                        isRecommendationView={true}
                        heading={
                            activeNarrativeTab === "mobile_narrative"
                                ? "Voice Notes"
                                : activeNarrativeTab === "ai_recom"
                                  ? "AI Recommendation"
                                  : "Recommendation Narrative"
                        }
                    />
                }
                onCancel={() => setShowNoteModal(false)}
            />
        );
    };
    const hiddenFieldsForAsset = ["asset_condition_id", "installed_year", "service_life", "usefull_life_remaining", "crv"];
    const isFillableField = field => shouldShowField(field) && !field?.rules?.read_only;
    const renderHighlightedLabel = labelValue => {
        const query = fieldSearch?.trim();
        const labelText = labelValue ?? "";
        if (!query) return labelText;
        return labelText.split(new RegExp(`(${query})`, "ig")).map((part, index) =>
            part.toLowerCase() === query.toLowerCase() ? (
                <span key={`${part}-${index}`} className="asset-field-search-label">
                    {part}
                </span>
            ) : (
                part
            )
        );
    };

    const memoizedAssetBands = useMemo(() => {
        const suggestionData = getSuggestionDropdown?.data ?? [];
        if (
            isEqual(prevDepsRef.current.data, data) &&
            isEqual(prevDepsRef.current.suggestionData, suggestionData) &&
            prevDepsRef.current.renderFormFields === renderFormFields &&
            prevDepsRef.current.fieldSearch === fieldSearch &&
            prevDepsRef.current.showType === showType &&
            isEqual(prevDepsRef.current.showFields, showFields) &&
            isEqual(prevDepsRef.current.showBand, showBand) &&
            prevDepsRef.current.shouldShowField === shouldShowField
        ) {
            return prevDepsRef.current.result;
        }
        if (!data && section === "edit") return [];
        const band = (
            Object.keys(data ?? {}).filter(eachBand => {
                if (eachBand.toLowerCase() === "check_list") return false;
                if (eachBand.toLowerCase() === "narrative") return false;
                if (eachBand.toLowerCase() === "asset") {
                    const recommendationType = data?.Recommendations?.recommendation_type?.value;
                    if (recommendationType !== "asset") return false;
                }
                const recommendationType = data?.Recommendations?.recommendation_type?.value?.toLowerCase();
                return Object.keys(data[eachBand] ?? {}).some(eachField => {
                    const field = data[eachBand][eachField];
                    if (!shouldShowField(field)) return false;
                    if (recommendationType === "asset" && hiddenFieldsForAsset.includes(eachField.toLowerCase())) return false;
                    const fieldLabel = field?.label ?? "";
                    const isFiltered = fieldLabel.toLowerCase().includes(fieldSearch.toLowerCase());
                    return showType ? isFiltered && showFields.includes(eachField) : isFiltered;
                });
            }) ?? []
        ).map((eachBand, index) => {
            const recommendationType = data?.Recommendations?.recommendation_type?.value?.toLowerCase();
            const bandFields = Object.entries(data?.[eachBand] ?? {}).filter(([eachKey, eachValue]) => {
                if (!shouldShowField(eachValue)) return false;
                const fieldName = eachKey.toLowerCase();
                const isFiltered = eachValue?.label?.toLowerCase().includes(fieldSearch?.toLowerCase());
                if (recommendationType === "asset" && hiddenFieldsForAsset.includes(fieldName)) {
                    return false;
                }
                return showType ? isFiltered && showFields.includes(eachKey) : isFiltered;
            });

            const headerElems = [];
            let usedHeaderCols = 0;
            const bodyElems = [];

            for (const [key, field] of bandFields) {
                const span = Math.min(Number(field.rules?.column_span) || 1, 4);
                const colSize = span * 3;
                if (usedHeaderCols + span <= 3) {
                    headerElems.push([key, field, colSize]);
                    usedHeaderCols += span;
                } else {
                    bodyElems.push([key, field]);
                }
            }

            const bodyRows = [];
            let currentRow = [];
            let currentSpan = 0;

            for (const [key, field] of bodyElems) {
                const span = Math.min(Number(field?.rules?.column_span) || 1, 4);
                if (currentSpan + span > 4) {
                    bodyRows.push(currentRow);
                    currentRow = [];
                    currentSpan = 0;
                }
                currentRow.push({ key, field, span });
                currentSpan += span;
            }

            if (currentRow.length) bodyRows.push(currentRow);
            return (
                <AssetBand
                    key={eachBand}
                    label={eachBand}
                    bandId={`band${index + 1}`}
                    handleBandClick={(band, value) => setShowBand({ ...showBand, [band]: value })}
                    isDefaultOpen
                    showBand={showBand}
                    isSurveyAsset={!isMobileRecommendation}
                    fillEmptyFields={() =>
                        setShowEmptyFieldsFillModal({
                            show: true,
                            fields: Object.keys(data?.[eachBand] ?? {}).filter(eachField => isFillableField(data?.[eachBand]?.[eachField]))
                        })
                    }
                    showFillEmptyField={Object.keys(data?.[eachBand] ?? {}).some(eachField => {
                        if (!isFillableField(data?.[eachBand]?.[eachField])) return false;
                        const suggestionValue = (getSuggestionDropdown?.data ?? [])?.find(e => e?.key === eachField)?.value;
                        return (
                            data?.[eachBand]?.[eachField]?.field_type === "text" &&
                            !data?.[eachBand]?.[eachField]?.value &&
                            (!suggestionValue || (suggestionValue && suggestionValue?.includes("NA")))
                        );
                    })}
                    MainFields={
                        <>
                            {eachBand === "Priority" &&
                                (fieldSearch === "" || "priority".includes(fieldSearch?.toLowerCase())) &&
                                (!showType || showType?.includes("priority")) && (
                                    <div className="txt-rcm col-md-3">
                                        <div className="content-inp-card blue-sec">
                                            <h3 className="p-name">{renderHighlightedLabel("Priority Total")}</h3>
                                            <h3 className="color-white">{data?.Priority?.["priority"]?.value || 0}</h3>
                                        </div>
                                    </div>
                                )}
                            {(headerElems || [])
                                ?.filter(e => e?.[0] !== "priority")
                                ?.map(([key, field, colSize]) => renderFormFields(key, field, eachBand, index, colSize))}
                            {Array.from({ length: 3 - usedHeaderCols }).map((_, i) => (
                                <div key={`hdr-fill-${i}`} className="txt-rcm col-md-3 mob-recom-field" />
                            ))}
                        </>
                    }
                    RestFields={
                        <>
                            {bodyRows.map((row, rowIdx) => (
                                <React.Fragment key={rowIdx}>
                                    {row.map(({ key, field, span }) => {
                                        return renderFormFields(key, field, eachBand, index, span * 3);
                                    })}

                                    {(() => {
                                        const used = row.reduce((sum, r) => sum + (r?.span || 0), 0);
                                        const cols = (4 - used) * 3;
                                        const shouldFill = eachBand?.toLowerCase() !== "Additional Details";
                                        return shouldFill && cols > 0 ? (
                                            <div className={`txt-rcm col-md-${cols} dynamic-cell mob-recom-field`} />
                                        ) : null;
                                    })()}
                                </React.Fragment>
                            ))}
                        </>
                    }
                />
            );
        });
        prevDepsRef.current = { data, suggestionData, renderFormFields, fieldSearch, showType, showFields, showBand, shouldShowField, result: band };
        return band;
    }, [data, fieldSearch, showType, showFields, showBand, getSuggestionDropdown?.data, renderFormFields, shouldShowField]);

    return (
        <>
            {renderNote()}
            <div className="col-md-8 asset-edit-port">
                <div id="accordion" ref={templateRef}>
                    {memoizedAssetBands}
                </div>
            </div>
            <div className="col-md-4 back-set">
                <ReactTooltip
                    id="recommendation_details"
                    effect="solid"
                    place="left"
                    backgroundColor="#007bff"
                    className="rc-tooltip-custom-class"
                    html={true}
                    getContent={dataTip => (dataTip ? sanitizeHtml(dataTip) : "")}
                    delayShow={500}
                />
                <div className="template pb-0 mob-narrative-template" style={{ height: "250px" }}>
                    <div className={`heading`}>
                        <div className="tabs new-ui-tab">
                            <button
                                type="button"
                                className={`tab-button ${activeNarrativeTab === "mobile_narrative" ? "active" : ""}`}
                                data-tip={`Mobile Recommendation Voice Note`}
                                data-for="recommendation_details"
                                onClick={() => setActiveNarrativeTab("mobile_narrative")}
                            >
                                Voice Notes
                            </button>
                            <button
                                type="button"
                                className={`tab-button ${activeNarrativeTab === "ai_narrative" ? "active" : ""}`}
                                data-tip={`AI Recommendation Narrative`}
                                data-for="recommendation_details"
                                onClick={() => setActiveNarrativeTab("ai_narrative")}
                            >
                                AI Rec. Narrative
                            </button>
                            <button
                                type="button"
                                className={`tab-button ${activeNarrativeTab === "ai_recom" ? "active" : ""}`}
                                data-tip={`AI Recommendation`}
                                data-for="recommendation_details"
                                onClick={() => setActiveNarrativeTab("ai_recom")}
                            >
                                AI Rec.
                            </button>
                        </div>
                        <div class="d-flex align-items-center">
                            <button
                                type="button"
                                className="btn"
                                data-tip={`Copy to Recommendation Narrative`}
                                data-for="recommendation_details"
                                onClick={() =>
                                    handleChange({
                                        name: "notes",
                                        value: editorData ? `${editorData} <br/><br/> ${audioText ?? ""}` : audioText ?? ""
                                    })
                                }
                            >
                                <img src={CopyIcon} alt="" srcset="" />
                            </button>
                            <div data-tip={`Click to Expand`} data-for="recommendation_details" data-effect="solid" class="ml-2 btn-expand-bl">
                                <img style={{ cursor: "pointer" }} onClick={() => openReportNote("mob-rec")} src="/img/expand1.svg" alt="" />
                            </div>
                        </div>
                    </div>
                    <div className={`cnt-temp-area notes-outer-area mb-recom-narrative`} data-for="recommendation_details">
                        <CKEditor
                            editor={Editor}
                            config={{ ...CkeditorConfiguration, toolbar: [] }}
                            id="mob-rec-editor"
                            data={audioText ?? ""}
                            onReady={editor => {
                                editor?.setData(audioText ? audioText : "");
                                editor?.enableReadOnlyMode("my-feature-id");
                            }}
                        />
                    </div>
                </div>

                <div className={`template templ-hgt`}>
                    <div className={`heading`}>
                        <h3 className={`p-0`}>Recommendation Narrative</h3>
                        <div class="d-flex align-items-center">
                            <div data-tip={`Click to Expand Recommendation Narrative`} data-for="recommendation_details" class="ml-2 btn-expand-bl">
                                <img style={{ cursor: "pointer" }} onClick={() => openReportNote("rec")} src="/img/expand1.svg" alt="" />
                            </div>
                        </div>
                    </div>
                    <div className={`cnt-temp-area notes-outer-area mob-normal-recom  `} style={{ maxHeight: `${editorHeight + 27}px` }}>
                        <CKEditor
                            editor={Editor}
                            config={{ ...CkeditorConfiguration, extraPlugins: [MyUploadAdapterPlugin] }}
                            data={editorData}
                            onChange={(event, editor) => {
                                const newData = editor.getData();
                                if (newData !== editorData) {
                                    setEditorData(newData);
                                    handleChange({ name: "notes", value: newData });
                                }
                            }}
                            onReady={editor => {
                                editor?.setData(data?.Narrative?.notes?.value ? data?.Narrative?.notes?.value : "");
                            }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

MobileRecommendationForm.propTypes = {};

export default MobileRecommendationForm;
